import { useMutation, useQuery } from "@apollo/client";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useNotify } from "react-admin";
import { ASYNC_TASK_STATUS, IMPORT_CLIENT_PORTFOLIO_RETURNS } from "./graphql";
import { monthArg, previousMonth } from "./logic";
import { MonthField, MultiFundPicker, Page, Section, useSelectedFunds } from "./shared";

const POLL_MS = 5000;

/** Polls the background task until it ends, as the risk refresh page does. */
function TaskStatus({ id }: { id: string }) {
  const { data, error, stopPolling } = useQuery(ASYNC_TASK_STATUS, { variables: { id }, pollInterval: POLL_MS });
  const task = data?.asyncTaskStatus;
  useEffect(() => {
    if (task?.endDate || error) stopPolling();
  }, [task?.endDate, error, stopPolling]);
  if (error) return <Alert severity="error">Couldn't check the import: {error.message}</Alert>;
  if (task?.endDate) {
    return task.error ? (
      <Alert severity="error">The import failed: {task.error}</Alert>
    ) : (
      <Alert severity="success">The import finished.</Alert>
    );
  }
  return <Alert severity="info">The import is queued and running in the background. You can leave this page.</Alert>;
}

export function ImportPortfolioReturns() {
  const notify = useNotify();
  const [fundIds, setFundIds] = useSelectedFunds();
  const [allFunds, setAllFunds] = useState(true);
  const [allDates, setAllDates] = useState(true);
  const [start, setStart] = useState(previousMonth());
  const [taskId, setTaskId] = useState<string | null>(null);
  const [run, running] = useMutation(IMPORT_CLIENT_PORTFOLIO_RETURNS);

  const onImport = async () => {
    try {
      const { data } = await run({
        variables: { rcgFundIds: allFunds ? null : fundIds, start: allDates ? null : monthArg(start) },
      });
      const id = data?.importClientPortfolioReturns.id;
      if (!id) throw new Error("The import was not queued");
      setTaskId(id);
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    }
  };

  return (
    <Page
      title="Import Portfolio Returns"
      description="Copies the funds' monthly returns from the NAV model, then refreshes their share classes. All funds and all dates can take a long time."
    >
      <Box sx={{ maxWidth: 900 }}>
        <Section title="What to import">
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2">Funds</Typography>
              <RadioGroup row value={allFunds ? "all" : "some"} onChange={e => setAllFunds(e.target.value === "all")}>
                <FormControlLabel value="all" control={<Radio />} label="All funds" />
                <FormControlLabel value="some" control={<Radio />} label="Specific funds" />
              </RadioGroup>
              {!allFunds && <MultiFundPicker value={fundIds} onChange={setFundIds} />}
            </Box>
            <Box>
              <Typography variant="subtitle2">Months</Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <RadioGroup row value={allDates ? "all" : "from"} onChange={e => setAllDates(e.target.value === "all")}>
                  <FormControlLabel value="all" control={<Radio />} label="All dates" />
                  <FormControlLabel value="from" control={<Radio />} label="Starting from" />
                </RadioGroup>
                {!allDates && <MonthField value={start} onChange={setStart} label="Start month" />}
              </Stack>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="contained" disabled={running.loading || (!allFunds && fundIds.length === 0)} onClick={onImport}>
                {running.loading ? "Queuing..." : "Import"}
              </Button>
              {!allFunds && fundIds.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Pick at least one fund.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Section>
      </Box>
      {taskId && <TaskStatus key={taskId} id={taskId} />}
    </Page>
  );
}
